/**
 * useKioskResetPairingListener — bridges the Electron tray menu's
 * "Kopplung zurücksetzen…" action to the server-side revoke RPC.
 *
 * Wiring:
 *   1. IT user picks the menu item → main.js shows a confirm dialog.
 *   2. On confirm, main fires `kiosk:reset-pairing-now` via webContents.send.
 *   3. This hook (mounted in any kiosk page that runs while a JWT exists)
 *      catches the event, asks the Supabase client built from the kiosk JWT
 *      to call `fn_kiosk_revoke_self`, and acks back through
 *      `window.kiosk.sendResetPairingResult` with `{ ok, status }`.
 *   4. Main logs the ack, clears native + localStorage, redirects to /kiosk/setup.
 *
 * Why renderer-driven?
 *   - Main has no Supabase client, no env config, no auth headers.
 *   - The renderer already imports and caches a kiosk-scoped supabase client
 *     keyed off the current JWT (see app/kiosk/utils/kioskUtils.ts).
 *   - Doing the RPC here keeps the secret in one place (the renderer) and
 *     avoids having to expose the URL/anon key (or the raw JWT itself) over
 *     IPC just so main can fire fetch().
 *
 * Failure modes handled:
 *   - No JWT in localStorage → ack `{ ok: false, reason: 'no jwt' }`. Main
 *     still proceeds with the local cleanup; nothing useful to revoke.
 *   - RPC returns an error → ack `{ ok: false, code, reason }`. Main still
 *     proceeds (the user explicitly asked to unpair; we don't want a server
 *     hiccup to leave the device half-unpaired locally).
 *   - Network down → caught, ack `{ ok: false, reason }`, main proceeds.
 *   - This hook unmounts during the round-trip → main's 8 s timeout fires
 *     and proceeds anyway.
 *
 * SAFE TO MOUNT MULTIPLE TIMES — only the first listener registered for the
 * IPC channel actually runs, because main uses `ipcMain.once(...)` to
 * collect the result and fires `webContents.send(...)` which delivers to
 * every listener; but onResetPairing's unsubscribe in the cleanup makes
 * sure stale listeners from unmounted components don't pile up.
 */
import { useEffect } from "react";

import { getKioskSupabaseClient, getKioskToken } from "../../../app/kiosk/utils/kioskUtils";

export function useKioskResetPairingListener() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const subscribe = window.kiosk?.onResetPairing;
    const ack = window.kiosk?.sendResetPairingResult;
    if (!subscribe || !ack) return; // running outside the Electron shell

    const unsubscribe = subscribe(async () => {
      const jwt = getKioskToken();
      if (!jwt) {
        ack({ ok: false, reason: "no jwt in localStorage" });
        return;
      }
      const client = getKioskSupabaseClient();
      if (!client) {
        ack({ ok: false, reason: "kiosk supabase client unavailable" });
        return;
      }
      try {
        const { error, status } = await client.rpc("fn_kiosk_revoke_self");
        if (error) {
          ack({
            ok: false,
            status,
            code: (error as { code?: string }).code,
            reason: error.message,
          });
          return;
        }
        ack({ ok: true, status });
      } catch (e) {
        ack({
          ok: false,
          reason: (e as { message?: string })?.message ?? "rpc threw",
        });
      }
    });

    return () => {
      if (typeof unsubscribe === "function") unsubscribe();
    };
  }, []);
}
